/**
 * SubtitleEdit — Custom edit view for the subtitle content type.
 *
 * Invariants:
 *   - `artifact.content` (raw VTT) is the master. Every mutation, from any
 *     source including the LLM, is a commit to it; the structured view and the
 *     player are derived. Undo/redo tracks it.
 *   - `localCues` is a working buffer, not a second source of truth. It commits
 *     on a debounce and on every semantic boundary (blur, add/remove/insert,
 *     mode switch, save).
 *   - Non-cue structure (header extras, STYLE/REGION/NOTE, per-cue settings)
 *     rides along untouched in `structuralRef`; the structured editor only ever
 *     edits start/end/text.
 *   - The player reflects; it never drives. It holds the last *valid* VTT, and
 *     is unmounted entirely while the document is invalid rather than left
 *     showing cues the user is no longer editing.
 */
import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Button, Message, Header } from 'semantic-ui-react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import Api from '@plone/volto/helpers/Api/Api';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import Sidebar from '@plone/volto/components/manage/Sidebar/Sidebar';
import InlineForm from '@plone/volto/components/manage/Form/InlineForm';
import { getSchema } from '@plone/volto/actions/schema/schema';
import useArtifact from 'volto-base-addon/hooks/useArtifact';
import VideoJS, {
  isAppleNativeHls,
} from 'volto-base-addon/components/manage/VideoJS/VideoJS';
import { tokenOf } from 'volto-base-addon/helpers/vocabulary';
import { formatFileSize } from 'volto-base-addon/helpers/fileSize';
import {
  SUBTITLE_METADATA_FIELDS,
  localizeLanguageChoices,
} from 'volto-base-addon/helpers/subtitleSchema';
import {
  parseVttDocument,
  serializeVttDocument,
  formatTimestamp,
  parseTimestamp,
  normalizeVtt,
  validateVtt,
  nextCueKey,
} from 'volto-base-addon/helpers/vttParser';

import saveSVG from '@plone/volto/icons/save.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import undoSVG from '@plone/volto/icons/undo.svg';
import redoSVG from '@plone/volto/icons/redo.svg';

// Debounce for auto-committing edits to the raw VTT master without a blur.
const COMMIT_DEBOUNCE_MS = 600;

// The banner sits in the sticky band, so its height is spent viewport. A broken
// paste yields one error per cue: show a few and count the rest.
const MAX_SHOWN_VTT_ERRORS = 5;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const messages = defineMessages({
  save: { id: 'SubtitleEdit.save', defaultMessage: 'Save' },
  cancel: { id: 'SubtitleEdit.cancel', defaultMessage: 'Cancel' },
  undo: { id: 'SubtitleEdit.undo', defaultMessage: 'Undo' },
  redo: { id: 'SubtitleEdit.redo', defaultMessage: 'Redo' },
  unsavedChanges: {
    id: 'SubtitleEdit.unsavedChanges',
    defaultMessage: 'You have unsaved changes. Discard?',
  },
  loadError: {
    id: 'SubtitleEdit.loadError',
    defaultMessage: 'Could not load subtitle content.',
  },
  saveError: {
    id: 'SubtitleEdit.saveError',
    defaultMessage: 'Failed to save. Please try again.',
  },
  invalidVtt: {
    id: 'SubtitleEdit.invalidVtt',
    defaultMessage: 'Cannot save — the subtitle content has errors:',
  },
  // One per `validateVtt` error id. The parser returns locale-free
  // descriptors; the ids must stay in sync with helpers/vttParser.js.
  vttErrorEmpty: {
    id: 'SubtitleEdit.vttError.empty',
    defaultMessage: 'The subtitle is empty.',
  },
  vttErrorMissingHeader: {
    id: 'SubtitleEdit.vttError.missingHeader',
    defaultMessage: 'The file must start with a WEBVTT header.',
  },
  vttErrorStrayContent: {
    id: 'SubtitleEdit.vttError.strayContent',
    defaultMessage:
      'Stray content (not a cue, comment, or style block): “{content}”',
  },
  vttErrorMalformedTimestampLine: {
    id: 'SubtitleEdit.vttError.malformedTimestampLine',
    defaultMessage: 'Cue {cue}: malformed timestamp line.',
  },
  vttErrorInvalidStartTimestamp: {
    id: 'SubtitleEdit.vttError.invalidStartTimestamp',
    defaultMessage: 'Cue {cue}: invalid start time “{timestamp}”.',
  },
  vttErrorInvalidEndTimestamp: {
    id: 'SubtitleEdit.vttError.invalidEndTimestamp',
    defaultMessage: 'Cue {cue}: invalid end time “{timestamp}”.',
  },
  vttErrorEndBeforeStart: {
    id: 'SubtitleEdit.vttError.endBeforeStart',
    defaultMessage: 'Cue {cue}: the end time must be after the start time.',
  },
  // Named for what the user does, not for the data format.
  structured: { id: 'SubtitleEdit.structured', defaultMessage: 'Interactive' },
  raw: { id: 'SubtitleEdit.raw', defaultMessage: 'Text' },
  moreErrors: {
    id: 'SubtitleEdit.moreErrors',
    defaultMessage: 'and {count} more errors',
  },
  noCues: {
    id: 'SubtitleEdit.noCues',
    defaultMessage: 'No cues yet.',
  },
  addCue: {
    id: 'SubtitleEdit.addCue',
    defaultMessage: 'Add cue',
  },
  insertCue: {
    id: 'SubtitleEdit.insertCue',
    defaultMessage: 'Insert cue below',
  },
  insertCueFirst: {
    id: 'SubtitleEdit.insertCueFirst',
    defaultMessage: 'Insert cue at start',
  },
  removeCue: {
    id: 'SubtitleEdit.removeCue',
    defaultMessage: 'Remove cue',
  },
  cues: { id: 'SubtitleEdit.cues', defaultMessage: 'Cues' },
  technicalDetails: {
    id: 'SubtitleEdit.technicalDetails',
    defaultMessage: 'Technical details',
  },
  previewUnavailable: {
    id: 'SubtitleEdit.previewUnavailable',
    defaultMessage:
      'In this browser the edited subtitle cannot be shown in the preview. It may still be available from the stream in the normal view.',
  },
  videoPlayer: {
    id: 'SubtitleEdit.videoPlayer',
    defaultMessage: 'Video player',
  },
  loading: { id: 'SubtitleEdit.loading', defaultMessage: 'Loading…' },
  editMode: { id: 'SubtitleEdit.editMode', defaultMessage: 'Edit mode' },
  subtitleCues: {
    id: 'SubtitleEdit.subtitleCues',
    defaultMessage: 'Subtitle cues',
  },
  rawVttContent: {
    id: 'SubtitleEdit.rawVttContent',
    defaultMessage: 'Text content',
  },
  cueAria: { id: 'SubtitleEdit.cueAria', defaultMessage: 'Cue {number}' },
  startTimeAria: {
    id: 'SubtitleEdit.startTimeAria',
    defaultMessage: 'Start time, cue {number}',
  },
  endTimeAria: {
    id: 'SubtitleEdit.endTimeAria',
    defaultMessage: 'End time, cue {number}',
  },
  textAria: {
    id: 'SubtitleEdit.textAria',
    defaultMessage: 'Text, cue {number}',
  },
  insertCueAria: {
    id: 'SubtitleEdit.insertCueAria',
    defaultMessage: 'Insert cue below, cue {number}',
  },
  removeCueAria: {
    id: 'SubtitleEdit.removeCueAria',
    defaultMessage: 'Remove cue, cue {number}',
  },
  editingAria: {
    id: 'SubtitleEdit.editingAria',
    defaultMessage: 'Editing: {title}',
  },
});

const VTT_ERROR_MESSAGES = {
  empty: messages.vttErrorEmpty,
  missingHeader: messages.vttErrorMissingHeader,
  strayContent: messages.vttErrorStrayContent,
  malformedTimestampLine: messages.vttErrorMalformedTimestampLine,
  invalidStartTimestamp: messages.vttErrorInvalidStartTimestamp,
  invalidEndTimestamp: messages.vttErrorInvalidEndTimestamp,
  endBeforeStart: messages.vttErrorEndBeforeStart,
};

// An unmapped id would be a parser/messages mismatch; show it rather than
// dropping the error silently.
function formatVttErrors(errors, intl) {
  const all = errors || [];
  const shown = all
    .slice(0, MAX_SHOWN_VTT_ERRORS)
    .map((error) =>
      VTT_ERROR_MESSAGES[error.id]
        ? intl.formatMessage(VTT_ERROR_MESSAGES[error.id], error.values)
        : error.id,
    );
  if (all.length > shown.length) {
    shown.push(
      intl.formatMessage(messages.moreErrors, {
        count: all.length - shown.length,
      }),
    );
  }
  return shown;
}

// ─── Cue Row ──────────────────────────────────────────────────────────

const CueRow = ({ cue, index, onChange, onBlur, onInsert, onRemove, intl }) => {
  const [startStr, setStartStr] = useState(formatTimestamp(cue.startTime));
  const [endStr, setEndStr] = useState(formatTimestamp(cue.endTime));

  // Sync from props when external changes arrive (undo/redo/raw edit)
  useEffect(() => {
    setStartStr(formatTimestamp(cue.startTime));
  }, [cue.startTime]);
  useEffect(() => {
    setEndStr(formatTimestamp(cue.endTime));
  }, [cue.endTime]);

  const handleStartBlur = () => {
    const parsed = parseTimestamp(startStr);
    setStartStr(formatTimestamp(parsed));
    onChange(index, { ...cue, startTime: parsed });
    onBlur();
  };

  const handleEndBlur = () => {
    const parsed = parseTimestamp(endStr);
    setEndStr(formatTimestamp(parsed));
    onChange(index, { ...cue, endTime: parsed });
    onBlur();
  };

  return (
    <li
      className="subtitle-cue-row"
      aria-label={intl.formatMessage(messages.cueAria, { number: index + 1 })}
    >
      <div className="subtitle-cue-timestamps">
        <input
          type="text"
          className="subtitle-cue-time"
          value={startStr}
          onChange={(e) => setStartStr(e.target.value)}
          onBlur={handleStartBlur}
          aria-label={intl.formatMessage(messages.startTimeAria, {
            number: index + 1,
          })}
          size={12}
        />
        <span className="subtitle-cue-arrow" aria-hidden="true">
          →
        </span>
        <input
          type="text"
          className="subtitle-cue-time"
          value={endStr}
          onChange={(e) => setEndStr(e.target.value)}
          onBlur={handleEndBlur}
          aria-label={intl.formatMessage(messages.endTimeAria, {
            number: index + 1,
          })}
          size={12}
        />
        <span className="subtitle-cue-actions">
          <button
            type="button"
            className="subtitle-cue-insert"
            onClick={() => onInsert(index)}
            aria-label={intl.formatMessage(messages.insertCueAria, {
              number: index + 1,
            })}
            title={intl.formatMessage(messages.insertCue)}
          >
            +
          </button>
          <button
            type="button"
            className="subtitle-cue-remove"
            onClick={() => onRemove(index)}
            aria-label={intl.formatMessage(messages.removeCueAria, {
              number: index + 1,
            })}
            title={intl.formatMessage(messages.removeCue)}
          >
            ✕
          </button>
        </span>
      </div>
      {/* The wrapper grows the field to fit its text; `rows` is only the
          floor. */}
      <div className="subtitle-cue-textwrap" data-replicated-value={cue.text}>
        <textarea
          className="subtitle-cue-text"
          value={cue.text}
          onChange={(e) => onChange(index, { ...cue, text: e.target.value })}
          onBlur={onBlur}
          rows={2}
          aria-label={intl.formatMessage(messages.textAria, {
            number: index + 1,
          })}
        />
      </div>
    </li>
  );
};

// ─── Compact Player ───────────────────────────────────────────────────
// Reflects the current VTT only. Does not seek or follow timepoints.

const CompactPlayer = ({ contentPath, subtitleVtt, subtitleLabel, intl }) => {
  const [streamUrl, setStreamUrl] = useState(null);

  // Injection is impossible on Apple, and the manifest's own tracks are a
  // different subtitle than the one being edited — so show none there.
  const appleNative = isAppleNativeHls();

  useEffect(() => {
    if (!contentPath) return;
    const api = new Api();
    api
      .get(`${contentPath}/@stream-url`)
      .then((resp) => resp?.stream_url && setStreamUrl(resp.stream_url))
      .catch(() => {});
  }, [contentPath]);

  // Build the single subtitle track from the current VTT editor state.
  // A new array identity on every VTT change makes VideoJS re-inject the track,
  // so the player always shows the latest cues (including cue settings, which
  // window.WebVTT.Parser applies visually).
  const subtitleTracks = React.useMemo(() => {
    if (appleNative || !subtitleVtt) return [];
    return [{ label: subtitleLabel || 'Subtitles', vttText: subtitleVtt }];
  }, [appleNative, subtitleVtt, subtitleLabel]);

  if (!streamUrl) return null;

  const sources = [{ src: streamUrl, type: 'application/x-mpegURL' }];

  return (
    <div
      className="subtitle-player"
      role="region"
      aria-label={intl.formatMessage(messages.videoPlayer)}
    >
      <VideoJS
        sources={sources}
        subtitleTracks={subtitleTracks}
        options={{ controls: true, fluid: true }}
        onReady={(player) => {
          const tracks = player.textTracks();
          const applyModes = () => {
            for (let i = 0; i < tracks.length; i++) {
              if (tracks[i].kind !== 'subtitles') continue;
              // On Apple these are the manifest's tracks, not the edited one.
              tracks[i].mode = appleNative ? 'disabled' : 'showing';
              if (!appleNative) break;
            }
          };
          applyModes();
          if (appleNative) {
            // Safari discovers the manifest's own subtitle renditions as it
            // parses the master playlist, which can land after loadedmetadata.
            // A one-shot pass would then miss them and leave the *saved*
            // subtitle rendering on top of the one being edited. Nothing on
            // Linux can reproduce this, so it is guarded rather than tested.
            tracks.addEventListener('addtrack', applyModes);
          }
        }}
      />
      {appleNative && (
        <p className="subtitle-player-notice">
          {intl.formatMessage(messages.previewUnavailable)}
        </p>
      )}
    </div>
  );
};

// ─── Sidebar Metadata ─────────────────────────────────────────────────

const SidebarMetadata = ({
  content,
  cueCount,
  intl,
  schema,
  formData,
  onChangeField,
}) => {
  const [technicalOpen, setTechnicalOpen] = useState(false);

  return (
    <div className="subtitle-sidebar-metadata">
      {schema ? (
        <InlineForm
          schema={schema}
          formData={formData}
          onChangeField={onChangeField}
        />
      ) : (
        <Header as="h2">{content?.title || ''}</Header>
      )}

      <div className="subtitle-sidebar-section">
        <div className="subtitle-sidebar-field">
          <strong>{intl.formatMessage(messages.cues)}</strong>
          <span>{cueCount}</span>
        </div>
      </div>

      <div
        className="subtitle-sidebar-accordion"
        role="button"
        tabIndex={0}
        aria-expanded={technicalOpen}
        aria-controls="subtitle-technical-details"
        onClick={() => setTechnicalOpen(!technicalOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setTechnicalOpen(!technicalOpen);
          }
        }}
      >
        <span>{intl.formatMessage(messages.technicalDetails)}</span>
        <span aria-hidden="true">{technicalOpen ? '▾' : '▸'}</span>
      </div>
      {technicalOpen && (
        <div
          id="subtitle-technical-details"
          className="subtitle-sidebar-section subtitle-sidebar-technical"
        >
          {content?.s3_object_id && (
            <div className="subtitle-sidebar-field">
              <strong>S3 ID</strong>
              <code>{content.s3_object_id}</code>
            </div>
          )}
          {content?.file_size && (
            <div className="subtitle-sidebar-field">
              <strong>Size</strong>
              <span>{formatFileSize(content.file_size)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Sidebar Metadata Portal (waits for #sidebar-metadata to exist) ───

const SidebarMetadataPortal = ({
  content,
  cueCount,
  intl,
  schema,
  formData,
  onChangeField,
}) => {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    // The Sidebar component creates #sidebar-metadata asynchronously.
    // Poll briefly until it appears, then render into it.
    let rafId;
    let attempts = 0;
    const check = () => {
      const el = document.getElementById('sidebar-metadata');
      if (el) {
        setTarget(el);
      } else if (attempts < 100) {
        attempts++;
        rafId = requestAnimationFrame(check);
      }
    };
    check();
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (!target) return null;

  return createPortal(
    <SidebarMetadata
      content={content}
      cueCount={cueCount}
      intl={intl}
      schema={schema}
      formData={formData}
      onChangeField={onChangeField}
    />,
    target,
  );
};

// ─── Main Component ───────────────────────────────────────────────────

const SubtitleEdit = () => {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();

  const content = useSelector((state) => state.content?.data);
  const contentPath = content ? flattenToAppURL(content['@id']) : null;
  const parentPath = content?.parent
    ? flattenToAppURL(content.parent['@id'])
    : null;

  const artifactId = contentPath ? `subtitle:${contentPath}` : null;
  const artifact = useArtifact(artifactId);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  // The backend's own words when it refuses a save — a cleared title reads far
  // better than a generic failure.
  const [saveErrorDetail, setSaveErrorDetail] = useState(null);

  // Structurally shaped by the shadowed schema reducer; only the locale-bound
  // part is left to do here, where there is an intl to do it with.
  const rawSchema = useSelector((state) => state.schema?.schema);
  const formSchema = useMemo(
    () => localizeLanguageChoices(rawSchema, intl.locale),
    [rawSchema, intl.locale],
  );
  // Sidebar metadata is edited here and saved with the cues, in one request and
  // one transaction.
  const [metadata, setMetadata] = useState({});
  const metadataRef = useRef({});
  const savedMetadataRef = useRef({});
  // Text mode is both the preferred default and the always-available surface,
  // so broken VTT already opens on the editor that can fix it.
  const [viewMode, setViewMode] = useState('raw');
  const [isClient, setIsClient] = useState(false);

  // Live validity of the raw VTT master. Raw is always editable; the structured
  // editor is gated on validity so it never operates on (and lossily mangles)
  // broken VTT. Errors surface in a persistent banner regardless of mode.
  const validation = useMemo(
    () =>
      loading ? { valid: true, errors: [] } : validateVtt(artifact.content),
    [artifact.content, loading],
  );
  const vttValid = validation.valid;

  // The player only ever reflects VALID VTT: while the master is invalid it
  // holds the last valid content instead of re-injecting broken cues.
  const [playerVtt, setPlayerVtt] = useState('');
  useEffect(() => {
    if (!loading && vttValid && artifact.content) {
      setPlayerVtt(artifact.content);
    }
  }, [artifact.content, vttValid, loading]);

  // Structured working buffer — a responsive copy of the raw VTT master.
  const [localCues, setLocalCues] = useState([]);
  // Latest cues, readable synchronously from debounce/timer callbacks.
  const localCuesRef = useRef([]);
  // Non-cue structure preserved verbatim (header + STYLE/REGION/NOTE blocks).
  const structuralRef = useRef({ header: 'WEBVTT', extras: [] });
  // What this component last pushed to the artifact — distinguishes our own
  // commits from external ones (undo/redo/LLM/load) in the sync effect.
  const lastCommittedRef = useRef(null);
  // What S3 currently holds, so a metadata-only save does not rewrite it.
  const savedContentRef = useRef(null);
  const textareaRef = useRef(null);
  // The cue <ol>, and the row whose text field should receive focus once the
  // next render has applied a structural change.
  const cueListRef = useRef(null);
  const pendingFocusRef = useRef(null);
  // Debounced-commit machinery.
  const debounceTimerRef = useRef(null);
  const pendingCommitRef = useRef(null);

  useEffect(() => {
    localCuesRef.current = localCues;
  }, [localCues]);

  useEffect(() => {
    metadataRef.current = metadata;
  }, [metadata]);

  // Seed the form once per subtitle: re-seeding on every content refresh would
  // discard whatever the user has typed but not yet saved.
  const seededForRef = useRef(null);
  useEffect(() => {
    const id = content?.['@id'];
    if (!id || seededForRef.current === id) return;
    const seed = {};
    SUBTITLE_METADATA_FIELDS.forEach((field) => {
      seed[field] = tokenOf(content[field]) ?? undefined;
    });
    seededForRef.current = id;
    savedMetadataRef.current = seed;
    setMetadata(seed);
  }, [content]);

  const handleMetadataChange = useCallback((id, value) => {
    setMetadata((current) => ({ ...current, [id]: value }));
  }, []);

  const changedMetadata = useCallback(() => {
    const changed = {};
    SUBTITLE_METADATA_FIELDS.forEach((field) => {
      const now = metadataRef.current[field] ?? null;
      const before = savedMetadataRef.current[field] ?? null;
      if (now !== before) changed[field] = now;
    });
    return changed;
  }, []);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // ─── Load schema for sidebar tab label ────────────────────────────

  useEffect(() => {
    if (content?.['@type']) {
      dispatch(getSchema(content['@type'], getBaseUrl(contentPath)));
    }
  }, [dispatch, content, contentPath]);

  // ─── Load VTT on mount ────────────────────────────────────────────

  useEffect(() => {
    if (!contentPath) return;
    const api = new Api();
    api
      .get(`${contentPath}/@subtitle-content`)
      .then((resp) => {
        const vtt = resp.content || 'WEBVTT\n\n';
        artifact.load(vtt);
        lastCommittedRef.current = vtt;
        savedContentRef.current = vtt;
        const doc = parseVttDocument(vtt);
        structuralRef.current = { header: doc.header, extras: doc.extras };
        setLocalCues(doc.cues);
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentPath]);

  // ─── Sync from artifact on EXTERNAL changes (undo/redo/LLM) ───────
  // Our own commits set lastCommittedRef first, so they are skipped here.

  useEffect(() => {
    if (!artifact.content) return;
    if (artifact.content !== lastCommittedRef.current) {
      lastCommittedRef.current = artifact.content;
      const doc = parseVttDocument(artifact.content);
      structuralRef.current = { header: doc.header, extras: doc.extras };
      setLocalCues(doc.cues);
      if (textareaRef.current) {
        textareaRef.current.value = artifact.content;
      }
    }
  }, [artifact.content]);

  // ─── Commit helpers ───────────────────────────────────────────────
  // Every mutation flows through here: raw VTT is the master, so we serialize
  // to VTT, push it to the artifact (one undo entry, marks dirty), and let the
  // artifact drive the player + structured view.

  const commitVtt = useCallback(
    (vtt, label) => {
      lastCommittedRef.current = vtt;
      artifact.commit(vtt, label);
    },
    [artifact],
  );

  // Structured buffer → raw VTT (reads refs so it is safe from timers).
  const commitStructured = useCallback(() => {
    const vtt = serializeVttDocument({
      ...structuralRef.current,
      cues: localCuesRef.current,
    });
    commitVtt(vtt, 'edit');
  }, [commitVtt]);

  // Raw textarea → raw VTT, then re-derive the structured buffer so the two
  // never drift. The textarea itself is left alone (the user may be typing).
  const commitRaw = useCallback(() => {
    if (!textareaRef.current) return;
    const value = textareaRef.current.value;
    commitVtt(value, 'edit');
    const doc = parseVttDocument(value);
    structuralRef.current = { header: doc.header, extras: doc.extras };
    setLocalCues(doc.cues);
  }, [commitVtt]);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingCommitRef.current = null;
  }, []);

  // Schedule a debounced commit; a fresh edit resets the timer.
  const scheduleCommit = useCallback((fn) => {
    pendingCommitRef.current = fn;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const f = pendingCommitRef.current;
      pendingCommitRef.current = null;
      if (f) f();
    }, COMMIT_DEBOUNCE_MS);
  }, []);

  // Flush any pending debounce immediately (blur / mode-switch / save).
  const flushCommit = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const f = pendingCommitRef.current;
    pendingCommitRef.current = null;
    if (f) f();
  }, []);

  // Clear the timer on unmount.
  useEffect(() => () => clearDebounce(), [clearDebounce]);

  // ─── Structured: update working buffer + schedule debounced commit ─

  const handleCueChange = useCallback(
    (index, updatedCue) => {
      setLocalCues((prev) => {
        const next = [...prev];
        next[index] = updatedCue;
        localCuesRef.current = next;
        return next;
      });
      scheduleCommit(commitStructured);
    },
    [scheduleCommit, commitStructured],
  );

  // Blur is a semantic boundary: flush pending work immediately.
  const handleCueBlur = useCallback(() => {
    flushCommit();
  }, [flushCommit]);

  // ─── Structural ops (immediate commit — each is its own undo entry) ─

  // Shift opaque extras so they stay anchored to the same cue after insert/remove.
  const shiftExtras = (extras, atIndex, delta) =>
    extras.map((e) =>
      e.beforeIndex > atIndex
        ? { ...e, beforeIndex: e.beforeIndex + delta }
        : e,
    );

  const commitCues = useCallback(
    (nextCues, label) => {
      setLocalCues(nextCues);
      localCuesRef.current = nextCues;
      commitVtt(
        serializeVttDocument({ ...structuralRef.current, cues: nextCues }),
        label,
      );
    },
    [commitVtt],
  );

  const handleAddCue = useCallback(() => {
    clearDebounce();
    const cues = localCuesRef.current;
    const lastCue = cues[cues.length - 1];
    const startTime = lastCue ? lastCue.endTime + 0.5 : 0;
    const newCue = {
      _key: nextCueKey(),
      id: '',
      startTime,
      endTime: startTime + 3,
      settings: '',
      text: '',
    };
    pendingFocusRef.current = cues.length;
    commitCues([...cues, newCue], 'add cue');
  }, [clearDebounce, commitCues]);

  const handleInsertCue = useCallback(
    (index) => {
      clearDebounce();
      const cues = localCuesRef.current;
      const cur = cues[index];
      const nextCue = cues[index + 1];
      const gap = 0.1;
      const startTime = cur ? cur.endTime + gap : 0;
      let endTime = startTime + 3;
      if (nextCue && nextCue.startTime > startTime) {
        endTime = Math.min(endTime, nextCue.startTime - gap);
      }
      if (endTime <= startTime) endTime = startTime + 1;
      const newCue = {
        _key: nextCueKey(),
        id: '',
        startTime,
        endTime,
        settings: '',
        text: '',
      };
      const next = [
        ...cues.slice(0, index + 1),
        newCue,
        ...cues.slice(index + 1),
      ];
      structuralRef.current = {
        ...structuralRef.current,
        extras: shiftExtras(structuralRef.current.extras, index, 1),
      };
      pendingFocusRef.current = index + 1;
      commitCues(next, 'insert cue');
    },
    [clearDebounce, commitCues],
  );

  const handleRemoveCue = useCallback(
    (index) => {
      clearDebounce();
      const cues = localCuesRef.current;
      const next = cues.filter((_, i) => i !== index);
      structuralRef.current = {
        ...structuralRef.current,
        extras: shiftExtras(structuralRef.current.extras, index, -1),
      };
      pendingFocusRef.current = index;
      commitCues(next, 'remove cue');
    },
    [clearDebounce, commitCues],
  );

  // Insert/remove rebuilds the list, so the button that was clicked is gone and
  // focus would fall to <body>. Move it to the text field of the cue the user
  // is now working on: the new one after an insert, the one that took the
  // removed cue's place after a remove (the last, if it was the last).
  useIsomorphicLayoutEffect(() => {
    const index = pendingFocusRef.current;
    if (index === null) return;
    pendingFocusRef.current = null;
    const fields =
      cueListRef.current?.querySelectorAll('.subtitle-cue-text') ?? [];
    if (!fields.length) return;
    fields[Math.min(index, fields.length - 1)].focus();
  });

  // ─── Raw mode handlers ────────────────────────────────────────────

  const handleRawChange = useCallback(() => {
    scheduleCommit(commitRaw);
  }, [scheduleCommit, commitRaw]);

  const handleRawBlur = useCallback(() => {
    flushCommit();
  }, [flushCommit]);

  // ─── Mode switching: commit pending work first ────────────────────

  const switchMode = useCallback(
    (newMode) => {
      if (newMode === viewMode) return;
      flushCommit();
      setViewMode(newMode);
    },
    [viewMode, flushCommit],
  );

  // Arrow keys move between tabs (WAI-ARIA tabs pattern); the structured tab
  // stays unreachable while the VTT is invalid, as with a pointer.
  const handleModeKeyDown = useCallback(
    (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const target = viewMode === 'structured' ? 'raw' : 'structured';
      if (target === 'structured' && !vttValid) return;
      e.preventDefault();
      switchMode(target);
      document.getElementById(`subtitle-mode-tab-${target}`)?.focus();
    },
    [viewMode, vttValid, switchMode],
  );

  // ─── Save ─────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!contentPath || saving) return;
    flushCommit();

    // Flush: derive current working state from the active mode.
    let currentContent;
    if (viewMode === 'structured') {
      currentContent = serializeVttDocument({
        ...structuralRef.current,
        cues: localCuesRef.current,
      });
    } else if (textareaRef.current) {
      currentContent = textareaRef.current.value;
    } else {
      currentContent = artifact.content;
    }

    // Validate (letting visual formatting pass through). Block on error and
    // route the user to raw — the always-available surface for fixing VTT.
    // The invalid content is still committed so the live error banner reflects
    // reality; raw is the master and may hold intermediate invalid states.
    const normalized = normalizeVtt(currentContent);
    const { valid } = validateVtt(normalized);
    if (!valid) {
      if (currentContent !== artifact.content) {
        commitVtt(currentContent, 'edit');
      }
      setViewMode('raw');
      return;
    }
    setSaveError(false);
    setSaveErrorDetail(null);
    setSaving(true);

    if (normalized !== artifact.content) {
      commitVtt(normalized, 'save');
    }

    const metadataChanges = changedMetadata();
    const contentChanged = normalized !== savedContentRef.current;
    const payload = {};
    if (contentChanged) payload.content = normalized;
    if (Object.keys(metadataChanges).length > 0) {
      payload.metadata = metadataChanges;
    }

    if (Object.keys(payload).length === 0) {
      artifact.setDirty(false);
      setSaving(false);
      history.push(contentPath);
      return;
    }

    try {
      const api = new Api();
      await api.post(`${contentPath}/@subtitle-save`, { data: payload });
      savedContentRef.current = normalized;
      savedMetadataRef.current = { ...metadataRef.current };
      artifact.setDirty(false);
      setSaving(false);
      history.push(contentPath);
    } catch (e) {
      setSaveError(true);
      setSaveErrorDetail(e?.response?.body?.error || null);
      setSaving(false);
    }
  }, [
    contentPath,
    saving,
    artifact,
    history,
    viewMode,
    flushCommit,
    commitVtt,
    changedMetadata,
  ]);

  // ─── Cancel ───────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (artifact.dirty || Object.keys(changedMetadata()).length > 0) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(intl.formatMessage(messages.unsavedChanges))) return;
    }
    clearDebounce();
    artifact.reset();
    history.push(parentPath || contentPath);
  }, [
    artifact,
    history,
    parentPath,
    contentPath,
    intl,
    clearDebounce,
    changedMetadata,
  ]);

  // ─── Keyboard shortcuts ───────────────────────────────────────────
  // Ctrl+S always saves. Ctrl+Z/Y only triggers artifact undo/redo
  // when no input is focused — lets browser native undo work in fields.

  useEffect(() => {
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      const inField = tag === 'TEXTAREA' || tag === 'INPUT';

      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if (
        !inField &&
        (e.ctrlKey || e.metaKey) &&
        e.key === 'z' &&
        !e.shiftKey
      ) {
        e.preventDefault();
        artifact.undo();
      }
      if (
        !inField &&
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.key === 'z' && e.shiftKey))
      ) {
        e.preventDefault();
        artifact.redo();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleSave, artifact]);

  // ─── beforeunload ─────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if (artifact.dirty || Object.keys(changedMetadata()).length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [artifact.dirty, changedMetadata]);

  // ─── Derived ──────────────────────────────────────────────────────

  const title = content?.title || '';
  // Save is offered for metadata edits too, not only cue edits.
  const metadataDirty = SUBTITLE_METADATA_FIELDS.some(
    (field) =>
      (metadata[field] ?? null) !== (savedMetadataRef.current[field] ?? null),
  );
  const unsaved = artifact.dirty || metadataDirty;
  const toolbarEl = isClient ? document.getElementById('toolbar') : null;
  const sidebarEl = isClient ? document.getElementById('sidebar') : null;

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div
      id="page-edit"
      className="subtitle-edit"
      role="main"
      aria-label={intl.formatMessage(messages.editingAria, { title })}
    >
      <Helmet title={`Edit: ${title}`} />

      {toolbarEl &&
        createPortal(
          <Toolbar
            pathname={contentPath || '/'}
            hideDefaultViewButtons
            inner={
              <>
                <Button
                  id="toolbar-save"
                  className="save"
                  aria-label={intl.formatMessage(messages.save)}
                  onClick={handleSave}
                  disabled={saving || !unsaved}
                  loading={saving}
                >
                  <Icon
                    name={saveSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.save)}
                  />
                </Button>
                <Button
                  className="cancel"
                  aria-label={intl.formatMessage(messages.cancel)}
                  onClick={handleCancel}
                >
                  <Icon
                    name={clearSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.cancel)}
                  />
                </Button>
                <Button
                  className="undo"
                  aria-label={intl.formatMessage(messages.undo)}
                  onClick={() => artifact.undo()}
                  disabled={!artifact.canUndo}
                >
                  <Icon
                    name={undoSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.undo)}
                  />
                </Button>
                <Button
                  className="redo"
                  aria-label={intl.formatMessage(messages.redo)}
                  onClick={() => artifact.redo()}
                  disabled={!artifact.canRedo}
                >
                  <Icon
                    name={redoSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.redo)}
                  />
                </Button>
              </>
            }
          />,
          toolbarEl,
        )}

      {sidebarEl &&
        createPortal(
          <Sidebar
            documentTab
            blockTab={false}
            orderTab={false}
            content={content}
          />,
          sidebarEl,
        )}

      <SidebarMetadataPortal
        content={content}
        cueCount={localCues.length}
        intl={intl}
        schema={formSchema}
        formData={metadata}
        onChangeField={handleMetadataChange}
      />

      {loadError && (
        <Message negative role="alert">
          {intl.formatMessage(messages.loadError)}
        </Message>
      )}
      {saveError && (
        <Message negative role="alert">
          {saveErrorDetail || intl.formatMessage(messages.saveError)}
        </Message>
      )}
      {loading ? (
        <div className="subtitle-edit-loading" aria-live="polite">
          {intl.formatMessage(messages.loading)}
        </div>
      ) : (
        !loadError && (
          <>
            {/* Everything the user needs while working a long cue list rides
                in one sticky band: the player, the surface switch, and the
                only feedback a refused Save gives. */}
            <div className="subtitle-sticky-head">
              {/* The player holds the last *valid* VTT, so while the document
                  is broken it shows cues that are no longer what the user is
                  looking at. Drop it rather than let it mislead — and the
                  space it frees is exactly what the error banner needs. */}
              {vttValid && (
                <CompactPlayer
                  contentPath={parentPath}
                  subtitleVtt={playerVtt}
                  subtitleLabel={title}
                  intl={intl}
                />
              )}

              <div
                className="subtitle-mode-toggle"
                role="tablist"
                aria-label={intl.formatMessage(messages.editMode)}
              >
                {/* Text first: it is the default, so it reads first too. */}
                <button
                  id="subtitle-mode-tab-raw"
                  onKeyDown={handleModeKeyDown}
                  role="tab"
                  aria-selected={viewMode === 'raw'}
                  aria-controls="subtitle-edit-panel"
                  tabIndex={viewMode === 'raw' ? 0 : -1}
                  className={`subtitle-mode-btn${viewMode === 'raw' ? ' active' : ''}`}
                  onClick={() => switchMode('raw')}
                  type="button"
                >
                  {intl.formatMessage(messages.raw)}
                </button>
                <button
                  id="subtitle-mode-tab-structured"
                  onKeyDown={handleModeKeyDown}
                  role="tab"
                  aria-selected={viewMode === 'structured'}
                  aria-controls="subtitle-edit-panel"
                  tabIndex={viewMode === 'structured' ? 0 : -1}
                  className={`subtitle-mode-btn${viewMode === 'structured' ? ' active' : ''}`}
                  onClick={() => switchMode('structured')}
                  type="button"
                  // Gate structured while the raw VTT is invalid, but never lock
                  // the user out of the tab they're already on.
                  disabled={!vttValid && viewMode !== 'structured'}
                  title={
                    !vttValid && viewMode !== 'structured'
                      ? intl.formatMessage(messages.invalidVtt)
                      : undefined
                  }
                >
                  {intl.formatMessage(messages.structured)}
                </button>
                {artifact.dirty && (
                  <span
                    className="subtitle-dirty-dot"
                    role="img"
                    aria-label={intl.formatMessage(messages.unsavedChanges)}
                    title={intl.formatMessage(messages.unsavedChanges)}
                  >
                    ●
                  </span>
                )}
              </div>

              {/* role="alert": Save silently refuses and switches to text mode
                  when the VTT is invalid, so this is the only feedback. Last in
                  the band, so it sits directly above the cues it describes.
                  Never coexists with the player. */}
              {!vttValid && (
                <Message
                  warning
                  role="alert"
                  className="subtitle-validation-error"
                >
                  <Message.Header>
                    {intl.formatMessage(messages.invalidVtt)}
                  </Message.Header>
                  <Message.List
                    items={formatVttErrors(validation.errors, intl)}
                  />
                </Message>
              )}
            </div>

            <div
              id="subtitle-edit-panel"
              className="subtitle-edit-content"
              role="tabpanel"
              aria-labelledby={`subtitle-mode-tab-${viewMode}`}
            >
              {viewMode === 'structured' ? (
                localCues.length > 0 ? (
                  <>
                    <button
                      type="button"
                      className="subtitle-add-cue subtitle-add-cue-top"
                      onClick={() => handleInsertCue(-1)}
                    >
                      + {intl.formatMessage(messages.insertCueFirst)}
                    </button>
                    <ol
                      ref={cueListRef}
                      className="subtitle-cue-list"
                      aria-label={intl.formatMessage(messages.subtitleCues)}
                    >
                      {localCues.map((cue, i) => (
                        <CueRow
                          key={cue._key}
                          cue={cue}
                          index={i}
                          onChange={handleCueChange}
                          onBlur={handleCueBlur}
                          onInsert={handleInsertCue}
                          onRemove={handleRemoveCue}
                          intl={intl}
                        />
                      ))}
                    </ol>
                  </>
                ) : (
                  // Empty subtitle: the only place the bottom "add cue" is
                  // needed — with cues present, per-row insert covers every spot.
                  <>
                    <p className="subtitle-no-cues">
                      {intl.formatMessage(messages.noCues)}
                    </p>
                    <button
                      type="button"
                      className="subtitle-add-cue"
                      onClick={handleAddCue}
                    >
                      + {intl.formatMessage(messages.addCue)}
                    </button>
                  </>
                )
              ) : (
                <textarea
                  ref={textareaRef}
                  className="subtitle-edit-textarea"
                  defaultValue={artifact.content || ''}
                  onChange={handleRawChange}
                  onBlur={handleRawBlur}
                  spellCheck={false}
                  aria-label={intl.formatMessage(messages.rawVttContent)}
                />
              )}
            </div>
          </>
        )
      )}
    </div>
  );
};

export default SubtitleEdit;
